import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmtEUR, tradeLabel } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    ArrowLeft,
    AlertTriangle,
    Sparkles,
    Loader2,
    FileSpreadsheet,
    FileText,
    Layers,
    Scissors,
    X,
    Pencil,
    Check,
    Trash2,
} from "lucide-react";
import { toast } from "sonner";

export default function ComparisonPage() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [duplicates, setDuplicates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detecting, setDetecting] = useState(false);
    const [project, setProject] = useState(null);
    const [selected, setSelected] = useState({}); // {item_id: {devis_id, row_key, trade, manual_group_id}}
    const [working, setWorking] = useState(false);

    const load = async () => {
        try {
            const [comp, dup, p] = await Promise.all([
                api.getComparison(id),
                api.listDuplicates(id),
                api.getProject(id),
            ]);
            setData(comp);
            setDuplicates(dup);
            setProject(p);
        } catch (e) {
            toast.error("Erreur de chargement");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [id]);

    const reload = async () => {
        const [comp, dup] = await Promise.all([
            api.getComparison(id),
            api.listDuplicates(id),
        ]);
        setData(comp);
        setDuplicates(dup);
    };

    const detect = async () => {
        setDetecting(true);
        try {
            const r = await api.detectDuplicates(id, "claude");
            setDuplicates(r.alerts);
            toast.success(`${r.count} doublon(s) détecté(s)`);
        } catch {
            toast.error("Erreur de détection");
        } finally {
            setDetecting(false);
        }
    };

    const toggleSelect = (itemId, meta) => {
        setSelected((prev) => {
            const next = { ...prev };
            if (next[itemId]) delete next[itemId];
            else next[itemId] = meta;
            return next;
        });
    };

    const clearSelection = () => setSelected({});

    const selectedItems = Object.entries(selected).map(([item_id, m]) => ({
        item_id,
        ...m,
    }));

    const fusionner = async () => {
        if (selectedItems.length < 2) return;
        // Only allow fusion of items inside the same trade
        const trades = new Set(selectedItems.map((s) => s.trade));
        if (trades.size > 1) {
            toast.error(
                "Fusion impossible : les postes doivent être dans le même corps de métier."
            );
            return;
        }
        setWorking(true);
        try {
            const itemIds = selectedItems.map((s) => s.item_id);
            await api.createManualGroup(id, itemIds, null);
            toast.success("Postes fusionnés sur une seule ligne");
            clearSelection();
            await reload();
        } catch (e) {
            toast.error("Erreur lors de la fusion");
        } finally {
            setWorking(false);
        }
    };

    const unmerge = async () => {
        if (selectedItems.length === 0) return;
        setWorking(true);
        try {
            await api.unmergeItems(
                id,
                selectedItems.map((s) => s.item_id)
            );
            toast.success("Fusion annulée");
            clearSelection();
            await reload();
        } catch {
            toast.error("Erreur");
        } finally {
            setWorking(false);
        }
    };

    if (loading) {
        return (
            <div className="p-10 text-sm text-ink-soft">Chargement…</div>
        );
    }
    if (!data || !project) return null;

    const dupItemMap = {};
    duplicates.forEach((d) => {
        dupItemMap[d.item_a_id] = d;
        dupItemMap[d.item_b_id] = d;
    });

    // Has any selected items inside a manual group?
    const anySelectedInGroup = selectedItems.some((s) => s.manual_group_id);

    return (
        <div className="min-h-screen pb-24">
            <header className="border-b border-border bg-white">
                <div className="max-w-[1600px] mx-auto px-10 py-7">
                    <Link
                        to={`/chantiers/${id}`}
                        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-ink-soft hover:text-ink mb-3"
                        data-testid="back-project"
                    >
                        <ArrowLeft className="w-3 h-3" />
                        {project.name}
                    </Link>
                    <div className="flex items-end justify-between gap-6">
                        <div>
                            <div className="stat-label mb-1.5">
                                03 — Comparaison
                            </div>
                            <h1 className="font-heading font-extrabold text-4xl tracking-tight">
                                Tableau comparatif des devis
                            </h1>
                            <p className="text-sm text-ink-soft mt-2 max-w-2xl">
                                Cliquez sur les cases pour les sélectionner,
                                puis <b>fusionnez</b> les postes équivalents
                                sur une même ligne. Astuce : sélectionnez
                                toutes les lignes d'un même entrepreneur qui
                                couvrent le poste pour qu'elles soient
                                additionnées correctement dans sa colonne.
                                Cliquez sur le crayon dans un montant pour
                                l'éditer.
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                onClick={() =>
                                    window.open(api.comparisonXlsxUrl(id), "_blank")
                                }
                                disabled={data.trades.length === 0}
                                className="rounded-none border-ink"
                                data-testid="export-xlsx-btn"
                            >
                                <FileSpreadsheet
                                    className="w-4 h-4 mr-2"
                                    strokeWidth={1.6}
                                />
                                Excel
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() =>
                                    window.open(api.comparisonPdfUrl(id), "_blank")
                                }
                                disabled={data.trades.length === 0}
                                className="rounded-none border-ink"
                                data-testid="export-pdf-btn"
                            >
                                <FileText
                                    className="w-4 h-4 mr-2"
                                    strokeWidth={1.6}
                                />
                                PDF
                            </Button>
                            <Button
                                onClick={detect}
                                disabled={detecting}
                                className="rounded-none bg-accent hover:bg-accent-hover"
                                data-testid="detect-duplicates-comparison"
                            >
                                {detecting ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Sparkles
                                        className="w-4 h-4 mr-2"
                                        strokeWidth={1.6}
                                    />
                                )}
                                Détecter les doublons (IA)
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-[1600px] mx-auto px-10 py-8 space-y-10">
                {data.trades.length === 0 && (
                    <div className="panel p-12 text-center">
                        <div className="font-heading text-xl mb-2">
                            Aucun devis à comparer
                        </div>
                        <p className="text-sm text-ink-soft">
                            Ajoutez des devis sur le chantier pour générer un
                            tableau comparatif.
                        </p>
                    </div>
                )}

                {data.trades.map((trade) => (
                    <TradeTable
                        key={trade.trade}
                        trade={trade}
                        dupItemMap={dupItemMap}
                        selected={selected}
                        onToggle={toggleSelect}
                        onUpdated={reload}
                    />
                ))}

                {Object.keys(data.global_totals).length > 0 && (
                    <section className="panel">
                        <div className="panel-header">
                            <h3 className="font-heading text-lg font-bold">
                                Récapitulatif global par devis
                            </h3>
                        </div>
                        <div className="divide-y divide-border">
                            {Object.entries(data.global_totals).map(
                                ([dvId, g]) => (
                                    <div
                                        key={dvId}
                                        className="px-6 py-3 flex items-center justify-between"
                                    >
                                        <div>
                                            <div className="font-semibold">
                                                {g.contractor_name}
                                            </div>
                                            <div className="text-xs text-ink-soft">
                                                {tradeLabel(g.trade)}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="num text-lg font-bold">
                                                {fmtEUR(g.total_ht)}
                                            </div>
                                            <div className="num text-xs text-ink-soft">
                                                TTC {fmtEUR(g.total_ttc)}
                                            </div>
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    </section>
                )}
            </main>

            {/* Floating selection bar */}
            {selectedItems.length > 0 && (
                <div
                    data-testid="selection-bar"
                    className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-ink text-white shadow-2xl border border-ink z-50 flex items-center gap-2 px-4 py-3 animate-fade-up"
                >
                    <span className="num font-bold mr-2">
                        {selectedItems.length}
                    </span>
                    <span className="text-sm">poste(s) sélectionné(s)</span>
                    <div className="w-px h-6 bg-white/20 mx-2" />
                    <Button
                        onClick={fusionner}
                        disabled={working || selectedItems.length < 2}
                        className="rounded-none bg-accent hover:bg-accent-hover text-white"
                        data-testid="merge-btn"
                    >
                        <Layers className="w-4 h-4 mr-2" strokeWidth={1.6} />
                        Fusionner sur 1 ligne
                    </Button>
                    {anySelectedInGroup && (
                        <Button
                            onClick={unmerge}
                            disabled={working}
                            variant="outline"
                            className="rounded-none border-white text-white bg-transparent hover:bg-white hover:text-ink"
                            data-testid="unmerge-btn"
                        >
                            <Scissors
                                className="w-4 h-4 mr-2"
                                strokeWidth={1.6}
                            />
                            Annuler la fusion
                        </Button>
                    )}
                    <button
                        onClick={clearSelection}
                        className="p-2 text-white/70 hover:text-white"
                        data-testid="clear-selection"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}
        </div>
    );
}

function TradeTable({ trade, dupItemMap, selected, onToggle, onUpdated }) {
    if (trade.devis.length === 0) return null;
    return (
        <section
            className="panel"
            data-testid={`trade-section-${trade.trade}`}
        >
            <div className="panel-header">
                <div>
                    <div className="stat-label">Corps de métier</div>
                    <h3 className="font-heading text-xl font-bold mt-0.5">
                        {tradeLabel(trade.trade)}{" "}
                        <span className="text-ink-soft font-normal text-sm">
                            · {trade.devis.length} devis · {trade.rows.length}{" "}
                            postes
                        </span>
                    </h3>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="bg-[#FAFAFA] border-b border-ink">
                            <th className="sticky left-0 z-10 bg-[#FAFAFA] text-left px-4 py-3 border-r border-border min-w-[280px] stat-label">
                                Poste
                            </th>
                            {trade.devis.map((d) => (
                                <th
                                    key={d.id}
                                    className="text-left px-4 py-3 border-r border-border min-w-[200px]"
                                >
                                    <div className="font-heading font-bold text-sm flex items-center gap-1.5">
                                        <span>{d.contractor_name}</span>
                                        {(d.version || 1) > 1 && (
                                            <span
                                                className="pill border-accent text-accent bg-accent/10 text-[10px] py-0"
                                                title={`Version ${d.version}`}
                                                data-testid={`comparison-version-pill-${d.id}`}
                                            >
                                                V{d.version}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[10px] text-ink-soft num">
                                        Total HT {fmtEUR(d.total_ht)}
                                    </div>
                                </th>
                            ))}
                            <th className="text-right px-4 py-3 stat-label min-w-[140px]">
                                Écart
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {trade.rows.map((row, idx) => {
                            const isDup = Object.values(row.values).some(
                                (v) => dupItemMap[v.item_id]
                            );
                            const isManualGroup = !!row.manual_group_id;
                            return (
                                <tr
                                    key={row.key || row.manual_group_id || `row-${idx}`}
                                    className={`border-b border-border align-top ${
                                        isDup ? "dup-row" : ""
                                    } ${
                                        isManualGroup
                                            ? "bg-accent/5"
                                            : ""
                                    }`}
                                    data-testid={`row-${trade.trade}-${idx}`}
                                >
                                    <td
                                        className={`sticky left-0 z-10 px-4 py-3 border-r border-border align-top ${
                                            isManualGroup
                                                ? "bg-accent/5"
                                                : "bg-white"
                                        }`}
                                    >
                                        <div className="flex items-start gap-2">
                                            {isDup && (
                                                <AlertTriangle
                                                    className="w-3.5 h-3.5 text-warn shrink-0 mt-0.5"
                                                    strokeWidth={2}
                                                />
                                            )}
                                            {isManualGroup && (
                                                <Layers
                                                    className="w-3.5 h-3.5 text-accent shrink-0 mt-0.5"
                                                    strokeWidth={2}
                                                />
                                            )}
                                            <div>
                                                <div className="text-sm font-medium leading-snug">
                                                    {row.label}
                                                </div>
                                                {row.unit && (
                                                    <div className="text-[10px] text-ink-soft uppercase mt-0.5">
                                                        unité {row.unit}
                                                    </div>
                                                )}
                                                {isManualGroup && (
                                                    <div className="text-[10px] text-accent uppercase mt-0.5 font-semibold">
                                                        Fusion manuelle
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    {trade.devis.map((d) => {
                                        const v = row.values[d.id];
                                        return (
                                            <td
                                                key={d.id}
                                                className="px-3 py-2 border-r border-border"
                                            >
                                                {v ? (
                                                    <EditableCell
                                                        devisId={d.id}
                                                        itemId={v.item_id}
                                                        quantity={v.quantity}
                                                        unitPrice={v.unit_price}
                                                        total={v.total}
                                                        details={v.details}
                                                        selected={!!selected[v.item_id]}
                                                        onSelect={() =>
                                                            onToggle(v.item_id, {
                                                                devis_id: d.id,
                                                                row_key: row.key,
                                                                trade: trade.trade,
                                                                manual_group_id: row.manual_group_id,
                                                            })
                                                        }
                                                        onSaved={onUpdated}
                                                    />
                                                ) : (
                                                    <span className="text-ink-soft/40 pl-2">
                                                        —
                                                    </span>
                                                )}
                                            </td>
                                        );
                                    })}
                                    <td className="px-4 py-3 text-right">
                                        {row.stats ? (
                                            <div>
                                                <div className="num text-sm font-bold text-danger">
                                                    {fmtEUR(row.stats.spread)}
                                                </div>
                                                <div className="num text-[10px] text-ink-soft">
                                                    moy {fmtEUR(row.stats.avg)}
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-ink-soft/40">
                                                —
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function EditableCell({
    devisId,
    itemId,
    quantity,
    unitPrice,
    total,
    details,
    selected,
    onSelect,
    onSaved,
}) {
    const [editing, setEditing] = useState(false);
    const [qty, setQty] = useState(quantity);
    const [pu, setPu] = useState(unitPrice);
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        setQty(quantity);
        setPu(unitPrice);
    }, [quantity, unitPrice]);

    const isMerged = Array.isArray(details) && details.length > 1;

    const startEdit = (e) => {
        e.stopPropagation();
        setEditing(true);
        setTimeout(() => inputRef.current?.focus(), 50);
    };

    const cancel = (e) => {
        e?.stopPropagation();
        setEditing(false);
        setQty(quantity);
        setPu(unitPrice);
    };

    const save = async (e) => {
        e?.stopPropagation();
        const q = parseFloat(String(qty).replace(",", ".")) || 0;
        const p = parseFloat(String(pu).replace(",", ".")) || 0;
        if (q === quantity && p === unitPrice) {
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            await api.updateLineItem(devisId, itemId, {
                quantity: q,
                unit_price: p,
            });
            toast.success("Poste mis à jour");
            setEditing(false);
            onSaved && onSaved();
        } catch {
            toast.error("Erreur d'enregistrement");
        } finally {
            setSaving(false);
        }
    };

    if (editing) {
        return (
            <div
                onClick={(e) => e.stopPropagation()}
                className="border-2 border-accent bg-white p-2 space-y-1"
            >
                <div className="grid grid-cols-2 gap-1">
                    <Input
                        ref={inputRef}
                        type="number"
                        value={qty}
                        onChange={(e) => setQty(e.target.value)}
                        className="rounded-none h-7 num text-right text-xs"
                        placeholder="Qté"
                        data-testid={`cell-edit-qty-${itemId}`}
                    />
                    <Input
                        type="number"
                        value={pu}
                        onChange={(e) => setPu(e.target.value)}
                        className="rounded-none h-7 num text-right text-xs"
                        placeholder="PU"
                        data-testid={`cell-edit-pu-${itemId}`}
                    />
                </div>
                <div className="flex gap-1">
                    <button
                        onClick={save}
                        disabled={saving}
                        className="flex-1 bg-ok text-white h-6 text-[11px] uppercase tracking-wide font-semibold hover:bg-ok/90 inline-flex items-center justify-center gap-1"
                        data-testid={`cell-save-${itemId}`}
                    >
                        {saving ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                            <Check className="w-3 h-3" strokeWidth={2.5} />
                        )}
                        OK
                    </button>
                    <button
                        onClick={cancel}
                        className="flex-1 border border-ink h-6 text-[11px] uppercase tracking-wide font-semibold hover:bg-ink hover:text-white"
                    >
                        Annuler
                    </button>
                </div>
            </div>
        );
    }

    return (
        <button
            onClick={onSelect}
            data-testid={`cell-${itemId}`}
            className={`w-full text-left rounded-none border ${
                selected
                    ? "border-accent bg-accent/10"
                    : "border-transparent hover:border-ink/30 hover:bg-[#FAFAFA]"
            } px-2 py-1.5 transition-colors group/cell`}
        >
            {isMerged ? (
                <div className="space-y-1">
                    <div className="space-y-0.5">
                        {details.map((dt, i) => (
                            <div
                                key={dt.item_id || i}
                                className="flex items-baseline justify-between gap-2 text-[11px] leading-tight"
                            >
                                <span
                                    className="text-ink-soft truncate"
                                    title={dt.description}
                                >
                                    {dt.description || "—"}
                                </span>
                                <span className="num font-medium shrink-0">
                                    {fmtEUR(dt.total)}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-ink/20 pt-1 flex items-center justify-between gap-2">
                        <span className="text-[10px] uppercase tracking-wide text-ink-soft font-semibold">
                            Sous-total
                        </span>
                        <span className="num text-sm font-bold">
                            {fmtEUR(total)}
                        </span>
                    </div>
                </div>
            ) : (
                <div className="flex items-start justify-between gap-1.5">
                    <div className="min-w-0">
                        <div className="num text-sm font-semibold">
                            {fmtEUR(total)}
                        </div>
                        <div className="num text-[11px] text-ink-soft">
                            {quantity} × {fmtEUR(unitPrice)}
                        </div>
                    </div>
                    <button
                        onClick={startEdit}
                        className="opacity-0 group-hover/cell:opacity-100 transition-opacity p-0.5 hover:text-accent"
                        title="Modifier"
                        data-testid={`cell-pencil-${itemId}`}
                    >
                        <Pencil className="w-3 h-3" strokeWidth={1.5} />
                    </button>
                </div>
            )}
        </button>
    );
}
